import { computed, onMounted, onBeforeUnmount } from 'vue';
import type { Ref } from 'vue';
import { createVSelectToggleHandlers } from '@mimir/web/utils/vselect-toggle-helper';
import type { VSelectToggleState } from '@mimir/web/utils/vselect-toggle-helper';

export interface FocusHandlerOptions {
  setFocused: (value: boolean) => void;
  getListeners: () => Record<string, unknown>;
}


export function createFocusBlurHandlers(options: FocusHandlerOptions) {
  const { setFocused, getListeners } = options;

  function handleFocus(event: FocusEvent): void {
    setFocused(true);
    const listeners = getListeners().focus;
    if (listeners) {
      if (Array.isArray(listeners)) {
        listeners.forEach(fn => fn(event));
      } else {
        (listeners as (event: FocusEvent) => void)(event);
      }
    }
  }

  function handleBlur(event: FocusEvent): void {
    setFocused(false);
    const listeners = getListeners().blur;
    if (listeners) {
      if (Array.isArray(listeners)) {
        listeners.forEach(fn => fn(event));
      } else {
        (listeners as (event: FocusEvent) => void)(event);
      }
    }
  }

  return {
    handleFocus,
    handleBlur,
  };
}

export interface VuetifyInputRef {
  activateMenu?: () => void;
  focus?: () => void;
  blur?: () => void;
  isMenuActive?: boolean;
  search?: string;
}

export function createListenersWithFocusHandlers(
  listeners: Record<string, unknown>,
  focusHandlers: ReturnType<typeof createFocusBlurHandlers> | null,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(listeners)) {
    if (key !== 'focus' && key !== 'blur') {
      result[key] = value;
    }
  }

  // Add our wrapped focus/blur handlers
  if (focusHandlers) {
    result.focus = focusHandlers.handleFocus;
    result.blur = focusHandlers.handleBlur;
  }

  return result;
}

// --- Style mode composable (shared by MSelect, MAutoComplete, MCombobox compat wrappers) ---

export interface MInputStyleProps {
  label: string | undefined;
  v2Style: boolean;
  highContrast: boolean;
  plain: boolean;
  compact: boolean;
  menuActive: boolean;
  variant: string | undefined;
  density: string;
  flat: boolean;
}

export type MInputClassPrefix = 'm-select' | 'm-autocomplete' | 'm-combobox';

export function useMInputStyle(
  props: MInputStyleProps,
  isFocused: Ref<boolean>,
  classPrefix: MInputClassPrefix,
  attrsClass: Ref<unknown>,
) {
  const isDefaultStyle = computed(() => !props.v2Style);

  const isDense = computed(() => props.density === 'compact');
  const isSolo = computed(() => {
    const v = props.variant;
    return v === 'solo' || v === 'solo-inverted' || v === 'solo-filled';
  });
  const isOutlined = computed(() => props.variant === 'outlined');

  const isPlain = computed(() => {
    if (isDefaultStyle.value) {
      return false;
    }
    return props.plain || (!isSolo.value && !props.flat && !isDense.value && !props.compact && !isOutlined.value);
  });

  const showLabel = computed(() => {
    const hasLabel = props.label !== undefined && props.label !== '';
    if (isDefaultStyle.value) {
      return hasLabel;
    }
    return !isPlain.value && hasLabel && (!isOutlined.value || isSolo.value);
  });

  const inputClasses = computed(() => {
    let base: string;
    if (isDefaultStyle.value) {
      base = `${classPrefix} m-default-style`;
    } else if (isPlain.value) {
      base = classPrefix;
    } else {
      base = `${classPrefix} m-common`;
    }

    return [
      base,
      !isDefaultStyle.value && props.label === undefined && `${classPrefix}-inline`,
      !isDefaultStyle.value && props.label !== undefined && isOutlined.value && isSolo.value && `${classPrefix}-inline-label`,
      !isDefaultStyle.value && props.compact && `${classPrefix}-compact`,
      props.menuActive && `${classPrefix}-menu-active`,
      attrsClass.value,
    ];
  });

  const labelClasses = computed(() => [
    isDefaultStyle.value ? 'm-default-style-label' : 'profile-label',
    isFocused.value && 'focused',
    props.highContrast && 'high-contrast',
    !isDefaultStyle.value && isOutlined.value && isSolo.value && 'muted',
  ]);

  function resolveVSelectLabel(): string | undefined {
    if (isDefaultStyle.value) {
      return undefined;
    }
    if (isOutlined.value && isSolo.value) {
      return undefined;
    }
    return props.label;
  }

  function resolveVSelectSolo(): boolean {
    if (isDefaultStyle.value) {
      return true;
    }
    if (isOutlined.value) {
      return false;
    }
    return isSolo.value || (!isPlain.value && props.label !== undefined && !isOutlined.value);
  }

  return {
    isDefaultStyle,
    isDense,
    isSolo,
    isOutlined,
    isPlain,
    showLabel,
    inputClasses,
    labelClasses,
    resolveVSelectLabel,
    resolveVSelectSolo,
  };
}

// --- Expose composable (shared by MSelect, MAutoComplete, MCombobox compat wrappers) ---

export function useVuetifyInputExpose(inputRef: Ref<VuetifyInputRef | null>) {
  function activateMenu(): void {
    inputRef.value?.activateMenu?.();
  }

  function focus(): void {
    inputRef.value?.focus?.();
  }

  function blur(): void {
    inputRef.value?.blur?.();
  }

  function getIsMenuActive(): boolean {
    return inputRef.value?.isMenuActive ?? false;
  }

  function setIsMenuActive(value: boolean): void {
    const ref = inputRef.value;
    if (ref && 'isMenuActive' in ref) {
      ref.isMenuActive = value;
    }
  }

  function getRef(): VuetifyInputRef | null {
    return inputRef.value;
  }

  return {
    activateMenu,
    focus,
    blur,
    getIsMenuActive,
    setIsMenuActive,
    getRef,
  };
}

// --- Shared utilities ---

export function omitKeys(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    if (!keys.includes(key)) {
      result[key] = obj[key];
    }
  }
  return result;
}

export type VuetifyInputVariant = 'outlined' | 'plain' | 'filled' | 'underlined' | 'solo' | 'solo-inverted' | 'solo-filled';
export type VuetifyInputDensity = 'default' | 'comfortable' | 'compact';

// --- Toggle handler lifecycle (shared by MSelect, MAutoComplete, MCombobox compat wrappers) ---

export function useToggleHandlers(
  inputRef: Ref<VuetifyInputRef | null>,
  getCloseOnClick: () => boolean,
): void {
  let handlers: ReturnType<typeof createVSelectToggleHandlers> | null = null;

  onMounted(() => {
    if (!getCloseOnClick()) {
      return;
    }
    const state: VSelectToggleState = {
      shouldPreventClick: false,
      selectRef: null,
      getSelectRef: () => inputRef.value,
    };
    handlers = createVSelectToggleHandlers(state);
    handlers.attachListeners();
  });

  onBeforeUnmount(() => {
    if (handlers) {
      handlers.detachListeners();
      handlers = null;
    }
  });
}

// --- V3 slot compatibility (shared by MSelect, MAutoComplete, MCombobox compat wrappers) ---

// V2 slot consumers receive items typed loosely; expose as `unknown` so consumers narrow.
function resolveItemRaw(internalItem: { raw: unknown }): unknown {
  return internalItem.raw;
}

export interface Vue3SlotCompatOptions {
  slots: Record<string, unknown>;
  getNormalSlots: () => Record<string, unknown>;
  getItems: () => unknown[] | undefined;
  getItemValueKey: () => string | ((item: unknown) => unknown) | undefined;
}

export function useVue3SlotCompat(options: Vue3SlotCompatOptions) {
  const { slots, getNormalSlots, getItems, getItemValueKey } = options;

  // Vuetify 2 looks up structural slots (`no-data`, `prepend-item`, `append-item`,
  // `prepend-inner`, ...) on `$slots`, not `$scopedSlots`. A `v-slot` template that declares a
  // scope variable is compiled without the `proxy` marker that reverse-proxies it onto `$slots`,
  // so slots arriving as normal slots must be re-emitted without a scope parameter to stay
  // visible to Vuetify 2.
  function v2NormalSlotNames(): string[] {
    return Object.keys(getNormalSlots()).filter(name => name !== 'infotext');
  }

  function v2ScopedSlotNames(): string[] {
    const normalSlotNames = new Set(v2NormalSlotNames());
    return Object.keys(slots).filter(name => name !== 'infotext' && !normalSlotNames.has(name));
  }

  // V3: item and selection slots need special unwrapping, so exclude them from generic forwarding.
  function vue3ForwardedSlotNames(): string[] {
    return Object.keys(slots).filter(name =>
      name !== 'infotext' && name !== 'item' && name !== 'selection'
    );
  }

  // Look up the full item from the items array since V3 InternalItem may lack properties.
  function resolveSelectionItem(internalItem: { value: unknown; raw: unknown }): unknown {
    const items = getItems();
    if (items === undefined || items.length === 0) {
      return internalItem.raw;
    }

    const key = getItemValueKey() ?? 'value';
    if (typeof key !== 'string') {
      return internalItem.raw;
    }
    return items.find(
      (item) => (item as Record<string, unknown>)[key] === internalItem.value,
    ) ?? internalItem.raw;
  }

  return {
    v2NormalSlotNames,
    v2ScopedSlotNames,
    vue3ForwardedSlotNames,
    resolveSelectionItem,
    resolveItemRaw,
  };
}
